import { z } from 'zod';
import { ORIENTATIONS } from '../constants.ts';
import { idSchema, unixMsSchema } from './common.ts';

export const orientationSchema = z.enum(ORIENTATIONS);

// Computed connectivity status returned by GET /v1/screens.
export const screenStatusSchema = z.enum(['online', 'offline', 'never_paired']);
export type ScreenStatus = z.infer<typeof screenStatusSchema>;

export const screenSchema = z.object({
  id: idSchema,
  org_id: idSchema,
  name: z.string().min(1),
  location: z.string().nullable(),
  orientation: orientationSchema,
  timezone: z.string().min(1), // IANA tz name, e.g. "America/Los_Angeles"
  paired_at: unixMsSchema.nullable(),
  last_seen_at: unixMsSchema.nullable(),
  player_version: z.string().nullable(),
  status: screenStatusSchema,
  // Per-screen overlay override (docs/09 §C2); null falls back to the org default.
  overlay_id: idSchema.nullable(),
  // Multi-zone layout assignment (Sprint 9, docs/09 §C1); null = fullscreen (today's
  // behavior). A dangling id (layout deleted after assignment) also resolves to
  // fullscreen — the manifest resolver tolerates it, it is not a schema-level concern.
  layout_id: idSchema.nullable(),
  created_at: unixMsSchema,
});
export type Screen = z.infer<typeof screenSchema>;

// GET /v1/screens embeds each screen's groups + last reported player state so the
// CMS table renders without N+1 calls.
export const screenGroupRefSchema = z.object({
  id: idSchema,
  name: z.string(),
});

export const screenLastStateSchema = z.object({
  current_item_id: z.string().nullable(),
  current_playlist_id: z.string().nullable(),
  current_playlist_name: z.string().nullable(),
  cache_ok: z.boolean(),
  uptime_s: z.number().int().nonnegative(),
  // Sprint 11 T2 (docs/04 §Storage durability): player-reported low-storage flag + usage
  // snapshot, from the heartbeat's X-Storage-Pressure/X-Storage-Usage headers. Absent when
  // the player hasn't reported it (older build, or the headers were malformed/missing).
  storage_pressure: z.boolean().optional(),
  storage_usage: z
    .object({ used: z.number().int().nonnegative(), quota: z.number().int().positive() })
    .optional(),
});
export type ScreenLastState = z.infer<typeof screenLastStateSchema>;

export const screenListItemSchema = screenSchema.extend({
  groups: z.array(screenGroupRefSchema),
  last_state: screenLastStateSchema.nullable(),
});
export type ScreenListItem = z.infer<typeof screenListItemSchema>;

export const createScreenSchema = z.object({
  name: z.string().min(1),
  location: z.string().optional(),
  orientation: orientationSchema.default('landscape'),
  timezone: z.string().min(1).default('America/Los_Angeles'),
});
export type CreateScreen = z.infer<typeof createScreenSchema>;

export const updateScreenSchema = createScreenSchema.partial().extend({
  // Assign (id) or clear (null) the screen's overlay override (docs/09 §C2). Validated
  // org-owned in the service. Absent = unchanged.
  overlay_id: idSchema.nullish(),
  // Assign (id) or clear (null) the screen's layout (Sprint 9). Validated org-owned in
  // the service. Absent = unchanged.
  layout_id: idSchema.nullish(),
});
export type UpdateScreen = z.infer<typeof updateScreenSchema>;

// POST /v1/screens/pair — bind a pairing code shown by a player to a screen.
export const pairScreenSchema = z.object({
  code: z.string(),
  screen_id: idSchema.optional(),
  new_screen: createScreenSchema.optional(),
});
export type PairScreen = z.infer<typeof pairScreenSchema>;
